import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Button } from "./ui/button";
import { Users, Loader2, Sparkles, GraduationCap } from "lucide-react";
import { api } from "../lib/api";
import { numberToHebrewLetter } from "../lib/hebrewNumerals";
import { toast } from "sonner";
import Markdown from "./Markdown";
import SaveToNotebookButton from "./SaveToNotebookButton";

/**
 * WhoSaysWhat — "מי אומר מה?" compact dialog that shows every selected
 * commentator's position on one verse, side by side, plus main difference /
 * shared idea / deep takeaway.
 *
 * All summaries are source-grounded: the LLM only gets commentary texts we
 * already fetched from Sefaria. Commentators without available text appear
 * with a transparent "not available" note rather than being faked.
 */
export default function WhoSaysWhat({
  open,
  onClose,
  book,
  bookHe,
  chapter,
  verse,       // number
  commentators,
  onBuildLesson,
}) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !book || !chapter || !verse) return;
    setData(null);
    setLoading(true);
    api
      .whoSaysWhat({
        book,
        chapter,
        verse,
        commentators: commentators && commentators.length ? commentators : undefined,
        provider: "anthropic",
      })
      .then(setData)
      .catch((e) => {
        const msg = e?.response?.data?.detail || e?.message || "שגיאה";
        toast.error(`שגיאה: ${msg}`);
      })
      .finally(() => setLoading(false));
  }, [open, book, chapter, verse, commentators]);

  const summary = data?.summary;
  const verseHe = data?.verse?.he || "";
  const refHe =
    data?.verse?.ref_he ||
    (book && chapter && verse ? `${bookHe || book} ${numberToHebrewLetter(chapter)}:${numberToHebrewLetter(verse)}` : "");

  const items = summary?.items || [];

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent
        dir="rtl"
        className="max-w-2xl md:max-w-3xl max-h-[90vh] overflow-y-auto bg-background border border-gold/30"
        data-testid="who-says-what-dialog"
      >
        <DialogHeader className="text-right">
          <DialogTitle className="font-heading text-2xl text-foreground flex items-center gap-2">
            <Users className="h-5 w-5 text-gold" strokeWidth={1.5} />
            מי אומר מה?
          </DialogTitle>
          {refHe && (
            <p className="font-latin italic text-xs text-muted-foreground tracking-widest">
              {refHe}
            </p>
          )}
        </DialogHeader>

        {/* Verse text */}
        {/* Verse text — preserve nikud + ta'amim */}
        {verseHe && (
          <p
            dir="rtl"
            data-testid="who-says-verse-text"
            className="torah-verse hebrew-text text-lg md:text-2xl text-foreground leading-loose text-center my-4"
          >
            {verseHe}
          </p>
        )}

        {loading && (
          <div className="flex flex-col items-center justify-center py-10 gap-3">
            <Loader2 className="h-6 w-6 animate-spin text-gold" strokeWidth={1.5} />
            <p className="font-heading text-sm text-muted-foreground">
              אוסף את דברי הפרשנים...
            </p>
          </div>
        )}

        {!loading && summary && (
          <>
            {/* Per-commentator focus cards — "המוקד של כל פרשן" */}
            {items.length > 0 && (
              <div className="space-y-3 mt-2" data-testid="who-says-items">
                <h4 className="font-heading text-sm tracking-[0.25em] uppercase text-gold">
                  המוקד של כל פרשן
                </h4>
                {items.map((it, i) => (
                  <div
                    key={i}
                    data-testid={`who-says-item-${(it.commentator || "x").replace(/ /g, "-")}`}
                    className="rounded-sm border border-gold/20 bg-card p-4"
                  >
                    <div className="flex items-baseline justify-between gap-2 mb-1.5">
                      <h5 className="font-heading text-base text-gold">
                        {it.commentator_he || it.commentator}
                      </h5>
                      <span className="font-latin italic text-[11px] text-muted-foreground tracking-wide">
                        {it.commentator}
                      </span>
                    </div>
                    {it.not_available ? (
                      <p className="text-sm text-muted-foreground italic">
                        אין פרשנות זמינה לפסוק זה.
                      </p>
                    ) : (
                      <Markdown className="body-text">{it.says}</Markdown>
                    )}
                  </div>
                ))}
              </div>
            )}

            {/* Empty state — server returned a summary but no per-commentator items */}
            {items.length === 0 && (
              <div
                data-testid="who-says-no-items"
                className="text-center text-sm text-muted-foreground border border-dashed border-border/60 rounded-sm px-4 py-6 mt-3"
              >
                לא נמצאו פירושים זמינים להשוואה לפסוק זה.
              </div>
            )}

            {/* Comparison blocks — Similarities / Differences */}
            <div className="mt-5 space-y-4">
              {summary.shared_idea && (
                <Block
                  title="נקודות דמיון"
                  testid="who-says-shared-idea"
                  tone="gold"
                >
                  {summary.shared_idea}
                </Block>
              )}
              {summary.main_difference && (
                <Block
                  title="נקודות מחלוקת"
                  testid="who-says-main-difference"
                  tone="gold"
                >
                  {summary.main_difference}
                </Block>
              )}
              {summary.deep_takeaway && (
                <Block title="לקח עמוק" testid="who-says-deep-takeaway">
                  {summary.deep_takeaway}
                </Block>
              )}
            </div>

            <div className="flex items-center justify-between mt-6 gap-3 flex-wrap">
              <p className="text-[11px] text-muted-foreground italic">
                סיכום מבוסס על המקורות בלבד · לא נוצר תוכן מומצא.
              </p>
              <div className="flex items-center gap-2 flex-wrap">
                {/* Save the entire compare summary to the notebook —
                    serializes the per-commentator focus + shared/diff
                    blocks as a single readable entry. */}
                <SaveToNotebookButton
                  variant="chip"
                  testIdSuffix="who-says-what"
                  payload={{
                    source_type: "ai",
                    source_book: book,
                    source_chapter: chapter,
                    source_verse: verse,
                    source_ref_he: refHe,
                    source_label: `מי אומר מה · ${refHe}`,
                    title: `מי אומר מה — ${refHe}`,
                    content_he: verseHe,
                    content_text: buildCompareSummaryText(verseHe, refHe, items, summary),
                  }}
                />
                {onBuildLesson && (
                  <Button
                    onClick={() => {
                      onClose?.();
                      onBuildLesson();
                    }}
                    data-testid="who-says-build-lesson"
                    className="sacred-btn-gold"
                  >
                    <GraduationCap className="h-4 w-4" strokeWidth={1.5} />
                    בנה שיעור
                  </Button>
                )}
              </div>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

function Block({ title, testid, tone = "default", children }) {
  const borderCls =
    tone === "gold" ? "border-gold/40 bg-gold/5" : "border-border/50";
  return (
    <div
      data-testid={testid}
      className={`rounded-sm border ${borderCls} p-4`}
    >
      <div className="flex items-center gap-2 mb-1.5">
        <Sparkles className="h-3.5 w-3.5 text-gold" strokeWidth={1.5} />
        <h6 className="font-heading text-sm text-foreground">{title}</h6>
      </div>
      <Markdown className="body-text">{children}</Markdown>
    </div>
  );
}

// Serialize the compare summary into a single multi-line text block,
// readable later as a notebook entry. Mirrors the on-screen sections so
// the saved note matches what the user just read.
function buildCompareSummaryText(verseHe, refHe, items, summary) {
  const lines = [];
  if (refHe) lines.push(`מי אומר מה — ${refHe}`);
  if (verseHe) lines.push("", verseHe);
  if (items && items.length) {
    lines.push("", "המוקד של כל פרשן:");
    for (const it of items) {
      const name = it.commentator_he || it.commentator || "";
      if (it.not_available) {
        lines.push(`• ${name}: אין פרשנות זמינה.`);
      } else {
        lines.push(`• ${name}: ${it.says || ""}`);
      }
    }
  }
  if (summary?.shared_idea) lines.push("", "נקודות דמיון:", summary.shared_idea);
  if (summary?.main_difference) lines.push("", "נקודות מחלוקת:", summary.main_difference);
  if (summary?.deep_takeaway) lines.push("", "לקח עמוק:", summary.deep_takeaway);
  return lines.join("\n");
}
